import { useEffect, useRef, useState } from 'react'
import { Loader, X, Inbox as InboxIcon, CheckCircle2 } from 'lucide-react'
import type { DashboardReview, ReviewStatus } from '../../types/dashboard'
import { isQualityOnlyEscalation, isUrgentEscalation } from '../../types/dashboard'

interface InboxViewProps {
  reviews: DashboardReview[]
  isLoading: boolean
  onApprove: (reviewId: string, responseText: string) => Promise<void>
  onReject: (reviewId: string) => Promise<void>
  onToast: (toast: { message: string; type: 'success' | 'danger' | 'info' }) => void
}

type Tab = 'needs_reply' | 'responded' | 'all'

const MAX_CHARS = 4096

const SENTIMENT_CLASSES: Record<string, string> = {
  positive: 'bg-rd-accent/10 text-rd-accent',
  neutral: 'bg-warning-500/10 text-warning-500',
  negative: 'bg-danger-500/10 text-danger-500',
}

const STATUS_CLASSES: Record<ReviewStatus, string> = {
  pending: 'bg-white/5 text-rd-muted',
  processing: 'bg-rd-accent2/10 text-rd-accent2',
  pending_approval: 'bg-warning-500/10 text-warning-500',
  responded: 'bg-rd-accent/10 text-rd-accent',
  escalated: 'bg-danger-500/10 text-danger-500',
  failed: 'bg-danger-500/10 text-danger-500',
  skipped: 'bg-white/5 text-rd-dim',
}

const STATUS_LABELS: Record<ReviewStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  pending_approval: 'Pending approval',
  responded: 'Posted',
  escalated: 'Escalated',
  failed: 'Failed',
  skipped: 'Skipped',
}

const DOT_CLASSES: Record<ReviewStatus, string> = {
  pending: 'bg-rd-dim',
  processing: 'bg-rd-accent2',
  pending_approval: 'bg-warning-500',
  responded: 'bg-rd-accent',
  escalated: 'bg-danger-500',
  failed: 'bg-danger-500',
  skipped: 'bg-rd-dim',
}

// A quality-only escalation (e.g. a 5-star review whose auto-generated reply
// came out too short) isn't a bad review — it shouldn't get the same red
// "something's wrong" treatment as a genuine negative-review escalation.
const QUALITY_ONLY_LABEL = 'Needs a quick look'
const QUALITY_ONLY_CLASS = 'bg-warning-500/10 text-warning-500'
const QUALITY_ONLY_DOT = 'bg-warning-500'

function statusLabel(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_LABEL : STATUS_LABELS[review.status]
}
function statusClass(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_CLASS : STATUS_CLASSES[review.status]
}
function dotClass(review: DashboardReview): string {
  return isQualityOnlyEscalation(review) ? QUALITY_ONLY_DOT : DOT_CLASSES[review.status]
}

function needsReply(r: DashboardReview): boolean {
  return r.status === 'pending_approval' || r.status === 'escalated'
}

function Stars({ rating, size = 'text-[11px]' }: { rating: number | null; size?: string }) {
  if (!rating) return <span className="text-rd-dim text-xs">—</span>
  return (
    <span className={`flex items-center gap-px ${size}`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span key={i} className={i < rating ? 'text-warning-500' : 'text-rd-dim'}>★</span>
      ))}
    </span>
  )
}

function initialsOf(name: string | null): string {
  return name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? '?'
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

const MIN_LIST_WIDTH = 240
const MAX_LIST_WIDTH = 520

export default function InboxView({ reviews, isLoading, onApprove, onReject, onToast }: InboxViewProps) {
  const [activeTab, setActiveTab] = useState<Tab>('needs_reply')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [responseText, setResponseText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [isRejecting, setIsRejecting] = useState(false)

  const [listWidth, setListWidth] = useState(320)
  const paneRef = useRef<HTMLDivElement>(null)
  const isDraggingRef = useRef(false)

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDraggingRef.current || !paneRef.current) return
      const rect = paneRef.current.getBoundingClientRect()
      const next = e.clientX - rect.left
      setListWidth(Math.min(MAX_LIST_WIDTH, Math.max(MIN_LIST_WIDTH, next)))
    }
    function handleMouseUp() {
      if (!isDraggingRef.current) return
      isDraggingRef.current = false
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const handleResizerMouseDown = () => {
    isDraggingRef.current = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const needsReplyList = reviews
    .filter(needsReply)
    .sort((a, b) => {
      const aUrgent = isUrgentEscalation(a)
      const bUrgent = isUrgentEscalation(b)
      if (aUrgent !== bUrgent) return aUrgent ? -1 : 1
      return (b.review_date ?? '').localeCompare(a.review_date ?? '')
    })
  const respondedList = [...reviews]
    .filter((r) => r.status === 'responded')
    .sort((a, b) => (b.review_date ?? '').localeCompare(a.review_date ?? ''))
  const allList = [...reviews].sort((a, b) => (b.review_date ?? '').localeCompare(a.review_date ?? ''))

  const list = activeTab === 'needs_reply' ? needsReplyList : activeTab === 'responded' ? respondedList : allList

  // Keep selection valid — auto-advance to the top of the queue when the
  // current item leaves the list (e.g. after approve/reject) or on tab switch.
  useEffect(() => {
    if (list.length === 0) {
      if (selectedId !== null) setSelectedId(null)
      return
    }
    if (!list.some((r) => r.id === selectedId)) {
      setSelectedId(list[0].id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, reviews])

  const selected = list.find((r) => r.id === selectedId) ?? null

  useEffect(() => {
    if (selected) {
      setResponseText(selected.generated_response ?? '')
      setIsEditing(false)
    }
  }, [selected?.id])

  const alreadyPosted = selected?.status === 'responded'
  const canAct = selected && selected.status !== 'responded' && selected.status !== 'skipped' && selected.status !== 'processing'
  const platformLabel = selected?.platform === 'judgeme' ? 'Judge.me' : 'Google'

  const handleApprove = async () => {
    if (!selected) return
    setIsApproving(true)
    try {
      await onApprove(selected.id, responseText)
      onToast({ message: `✓ Reply posted to ${platformLabel} successfully`, type: 'success' })
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to post reply', type: 'danger' })
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    if (!selected) return
    setIsRejecting(true)
    try {
      await onReject(selected.id)
      onToast({ message: 'Review moved to manual queue', type: 'info' })
    } catch (err) {
      onToast({ message: err instanceof Error ? err.message : 'Failed to reject review', type: 'danger' })
    } finally {
      setIsRejecting(false)
    }
  }

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: 'needs_reply', label: 'Needs reply', count: needsReplyList.length },
    { id: 'responded', label: 'Responded', count: respondedList.length },
    { id: 'all', label: 'All', count: allList.length },
  ]

  const sentiment = selected?.intent_analysis?.sentiment
  const severity = selected?.escalation_result?.severity

  return (
    <div ref={paneRef} className="flex-1 flex overflow-hidden bg-rd-surface border border-white/[0.08] rounded-xl min-h-0 m-4">

      {/* Conversation list */}
      <div style={{ width: listWidth }} className="flex-shrink-0 flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-white/[0.07] flex-shrink-0">
          <h2 className="text-[15px] font-medium text-rd-text">Inbox</h2>
          <p className="text-[11px] text-rd-dim mt-0.5">All reviews, every connected platform, in one place</p>
        </div>

        <div className="flex gap-1 px-3 py-2 border-b border-white/[0.07] flex-shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                activeTab === t.id
                  ? 'bg-rd-accent/10 text-rd-accent'
                  : 'text-rd-muted hover:bg-rd-surface2 hover:text-rd-text'
              }`}
            >
              {t.label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === t.id ? 'bg-rd-accent/15' : 'bg-white/5'}`}>
                {t.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin">
          {isLoading && (
            <div className="flex flex-col gap-0">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="px-4 py-3 border-b border-white/[0.04] animate-pulse">
                  <div className="h-3 w-32 bg-white/5 rounded mb-2" />
                  <div className="h-2.5 w-full bg-white/[0.03] rounded" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && list.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-center px-4">
              {activeTab === 'needs_reply' ? (
                <>
                  <CheckCircle2 className="w-7 h-7 text-rd-accent mb-2" />
                  <p className="text-rd-muted text-[13px]">All caught up!</p>
                  <p className="text-rd-dim text-[11px] mt-1">No reviews waiting on a reply.</p>
                </>
              ) : (
                <p className="text-rd-muted text-[13px]">Nothing here yet</p>
              )}
            </div>
          )}

          {!isLoading && list.map((review) => {
            const isSelected = review.id === selectedId
            return (
              <button
                key={review.id}
                type="button"
                onClick={() => setSelectedId(review.id)}
                className={`w-full text-left px-4 py-3 border-b border-white/[0.04] transition-colors ${
                  isSelected ? 'bg-rd-accent/[0.06]' : 'hover:bg-rd-surface2/60'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${needsReply(review) ? dotClass(review) : 'bg-transparent'}`} />
                  <p className="text-[12.5px] font-medium text-rd-text truncate flex-1">
                    {review.reviewer_name ?? 'Anonymous'}
                  </p>
                  {review.platform === 'judgeme' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-primary-500/10 text-primary-500 shrink-0">
                      Judge.me
                    </span>
                  )}
                  <span className="text-[10px] text-rd-dim flex-shrink-0">{timeAgo(review.review_date)}</span>
                </div>
                <div className="flex items-center gap-2 pl-3.5">
                  <Stars rating={review.rating} />
                  <p className="text-[11px] text-rd-muted truncate flex-1">
                    {review.review_text ?? '(no review text)'}
                  </p>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Resizer */}
      <div
        onMouseDown={handleResizerMouseDown}
        role="separator"
        aria-orientation="vertical"
        title="Drag to resize"
        className="w-1 flex-shrink-0 cursor-col-resize bg-white/[0.08] hover:bg-rd-accent/50 active:bg-rd-accent/70 transition-colors"
      />

      {/* Reading pane */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {!selected && (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <InboxIcon className="w-8 h-8 text-rd-dim mb-3" />
            <p className="text-rd-muted text-sm">Select a conversation to view the thread</p>
          </div>
        )}

        {selected && (
          <>
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4 border-b border-white/[0.08] flex-shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-[14px] font-medium text-rd-text">{selected.reviewer_name ?? 'Anonymous'}</p>
                  {selected.platform === 'judgeme' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-500/10 text-primary-500">
                      Judge.me
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-rd-dim mt-0.5">
                  {selected.review_date
                    ? new Date(selected.review_date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
                    : 'Date unknown'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {sentiment && sentiment in SENTIMENT_CLASSES && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${SENTIMENT_CLASSES[sentiment]}`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                  </span>
                )}
                {severity && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/5 text-rd-muted">
                    {severity.charAt(0).toUpperCase() + severity.slice(1)} severity
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClass(selected)}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current" />
                  {statusLabel(selected)}
                </span>
              </div>
            </div>

            {/* Thread */}
            <div className="flex-1 overflow-y-auto px-5 py-5 flex flex-col gap-4 scrollbar-thin">

              {/* Incoming — the review */}
              <div className="flex items-start gap-2.5 max-w-[80%]">
                <div className="w-8 h-8 rounded-full bg-primary-500/15 border border-primary-500/20 flex items-center justify-center text-[11px] font-medium text-primary-500 flex-shrink-0">
                  {initialsOf(selected.reviewer_name)}
                </div>
                <div className="bg-rd-surface2 border border-white/[0.07] rounded-2xl rounded-tl-sm p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Stars rating={selected.rating} size="text-sm" />
                  </div>
                  <p className="text-[13px] text-rd-muted leading-relaxed">
                    {selected.review_text ?? '(No review text)'}
                  </p>
                </div>
              </div>

              {/* Outgoing — the reply */}
              <div className="max-w-[80%] ml-auto w-full">
                <div className="bg-rd-accent/[0.04] border border-rd-accent/20 rounded-2xl rounded-tr-sm p-4">
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-rd-accent animate-pulse" />
                    <span className="text-[10px] text-rd-accent uppercase tracking-wider font-medium">ReputeDesk AI draft</span>
                  </div>
                  <textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    disabled={!isEditing || Boolean(alreadyPosted)}
                    rows={6}
                    maxLength={MAX_CHARS}
                    className="w-full bg-transparent border-none outline-none text-rd-muted text-[13px] leading-relaxed resize-none disabled:cursor-default"
                    placeholder={alreadyPosted ? 'Response already posted.' : '(No AI response generated for this review — write one below)'}
                  />
                  <p className={`text-[10px] text-right mt-1 font-mono ${responseText.length > 3800 ? 'text-warning-500' : 'text-rd-dim'}`}>
                    {responseText.length} / {MAX_CHARS}
                  </p>
                </div>

                {alreadyPosted && (
                  <p className="text-center text-[12px] text-rd-accent mt-2">
                    ✓ Reply posted to {platformLabel}
                    {selected.response_posted_at && (
                      <span className="text-rd-dim ml-1">
                        · {new Date(selected.response_posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Actions */}
            {canAct && (
              <div className="flex flex-col gap-2 px-5 py-4 border-t border-white/[0.08] flex-shrink-0">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={isApproving || isRejecting || !responseText.trim()}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium bg-rd-accent/15 border border-rd-accent/30 text-rd-accent hover:bg-rd-accent/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isApproving ? <Loader className="w-3.5 h-3.5 animate-spin" /> : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {isApproving ? 'Posting…' : `Post reply to ${platformLabel}`}
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    disabled={isApproving || isRejecting || isEditing}
                    className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-[13px] font-medium bg-primary-500/10 border border-primary-500/25 text-primary-500 hover:bg-primary-500/15 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    Edit
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleReject}
                  disabled={isApproving || isRejecting}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-[13px] font-medium bg-danger-500/8 border border-danger-500/20 text-danger-500 hover:bg-danger-500/12 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isRejecting ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  {isRejecting ? 'Rejecting…' : 'Reject — send to manual queue'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
